/**
 * Combined icon & color picker for chat agents.
 * Opens a single popover from the agent avatar preview,
 * showing both icon and color grids in a compact layout.
 */

import * as React from 'react';
import { Pencil } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { AGENT_ICONS, getAgentIcon } from './icon-picker';
import { AGENT_COLORS, getAgentColorBg } from './agent-color-picker';

interface AgentIconColorPickerProps {
  icon: string;
  color: string;
  onIconChange: (icon: string) => void;
  onColorChange: (color: string) => void;
  disabled?: boolean;
}

export function AgentIconColorPicker({
  icon,
  color,
  onIconChange,
  onColorChange,
  disabled = false,
}: AgentIconColorPickerProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const Icon = getAgentIcon(icon);
  const bg = getAgentColorBg(color);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button
          type='button'
          disabled={disabled}
          className={cn(
            'relative flex size-10 shrink-0 items-center justify-center rounded-lg transition-colors cursor-pointer',
            'hover:ring-2 hover:ring-ring hover:ring-offset-1',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
            !bg && 'bg-muted',
          )}
          style={bg ? { backgroundColor: bg } : undefined}
        >
          <Icon
            className={cn('size-5', !color && 'text-muted-foreground')}
            style={color ? { color } : undefined}
          />
          <span className='absolute -bottom-1 -right-1 flex size-4 items-center justify-center rounded-full border bg-background shadow-sm'>
            <Pencil className='size-2.5 text-muted-foreground' />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className='w-72 p-3' align='start'>
        {/* Icon grid */}
        <h4 className='text-xs font-medium text-muted-foreground mb-2'>Icon</h4>
        <div className='grid grid-cols-8 gap-1'>
          {Object.entries(AGENT_ICONS).map(([name, IconOption]) => (
            <button
              key={name}
              type='button'
              onClick={() => onIconChange(name)}
              className={cn(
                'flex items-center justify-center size-8 rounded-md transition-colors',
                'hover:bg-accent hover:text-accent-foreground',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                icon === name
                  ? 'bg-primary/10 text-primary ring-1 ring-primary/30'
                  : 'text-muted-foreground',
              )}
              title={name}
            >
              <IconOption className='size-4' />
            </button>
          ))}
        </div>

        {/* Divider */}
        <div className='my-3 border-t' />

        {/* Color grid */}
        <h4 className='text-xs font-medium text-muted-foreground mb-2'>
          Color
        </h4>
        <div className='grid grid-cols-8 gap-1.5'>
          {/* None / reset option */}
          <button
            type='button'
            onClick={() => onColorChange('')}
            className={cn(
              'size-7 rounded-full border bg-muted transition-transform hover:scale-110',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
              !color
                ? 'ring-2 ring-ring ring-offset-1 border-transparent'
                : 'border-border/40',
            )}
            title='None'
          />
          {AGENT_COLORS.map((c) => (
            <button
              key={c.value}
              type='button'
              onClick={() => onColorChange(c.value)}
              className={cn(
                'size-7 rounded-full border transition-transform hover:scale-110',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                color === c.value
                  ? 'ring-2 ring-ring ring-offset-1 border-transparent'
                  : 'border-border/40',
              )}
              style={{ backgroundColor: c.value }}
              title={c.name}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
